import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ChevronDownLine,
  ChevronLeftLine,
  ChevronRightLine,
  ChevronUpLine,
} from '@dropbox/dig-icons/assets';
import {Title} from 'components/DSYS/Title';
import {t} from 'i18next';
import {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';

import {AppCatalogCard} from './AppCatalogCard';
import styles from './Apps.module.css';
import {CatalogApp} from './catalogApp';
import {OrgAccent} from './catalogOrgs';
import {useWheelAxisLock} from './useWheelAxisLock';

const getRailOverflow = (rail: HTMLDivElement) => {
  const maxScrollLeft = Math.max(rail.scrollWidth - rail.clientWidth, 0);
  return {
    left: rail.scrollLeft > 1,
    right: rail.scrollLeft < maxScrollLeft - 1,
  };
};

/**
 * One org's apps as a titled horizontal row. When it overflows, arrows page through it and
 * "See all" wraps it into rows in place. An `accent` faintly tints the shelf's cards.
 */
export const AppShelf = forwardRef<
  HTMLElement,
  {accent?: OrgAccent; apps: CatalogApp[]; heading: string; withActions?: boolean}
>(({accent, apps, heading, withActions}, ref) => {
  const sectionRef = useRef<HTMLElement>(null);
  useImperativeHandle(ref, () => sectionRef.current as HTMLElement);
  const railRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({left: false, right: false});
  const [expanded, setExpanded] = useState(false);
  const hasOverflow = overflow.left || overflow.right;
  const canExpand = hasOverflow || expanded;
  useWheelAxisLock(railRef);

  const updateOverflow = useCallback(() => {
    if (railRef.current) setOverflow(getRailOverflow(railRef.current));
  }, []);

  const toggleExpanded = () => {
    if (!canExpand) return;
    setExpanded((current) => !current);
    // Collapsing a long grid can leave the viewer far below this shelf; bring its heading back.
    const section = sectionRef.current;
    if (expanded && section && section.getBoundingClientRect().top < 0) {
      section.scrollIntoView?.({block: 'start'});
    }
  };

  useEffect(() => {
    updateOverflow();
    const rail = railRef.current;
    if (!rail || typeof ResizeObserver === 'undefined') return;
    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(rail);
    return () => resizeObserver.disconnect();
  }, [apps, expanded, updateOverflow]);

  const page = (direction: 'left' | 'right') => {
    const rail = railRef.current;
    if (!rail) return;
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    // The rail bleeds past the column, so page by the column's width (one full set of tiles),
    // landing on a tile edge even after a free trackpad scroll.
    const pageWidth = (rail.parentElement?.clientWidth ?? rail.clientWidth) + gap;
    const tileStride = ((rail.firstElementChild as HTMLElement | null)?.offsetWidth ?? 0) + gap;
    const target = rail.scrollLeft + (direction === 'left' ? -pageWidth : pageWidth);
    const left = tileStride > gap ? Math.round(target / tileStride) * tileStride : target;
    if (rail.scrollTo) {
      rail.scrollTo({left, behavior: 'smooth'});
    } else {
      rail.scrollLeft = left;
    }
  };

  return (
    <section ref={sectionRef} aria-label={heading} className={styles.appShelf} data-accent={accent}>
      <Split alignY="center" gap="8">
        <Split.Item width="fill">
          <Title size={20} tagName="h3">
            {canExpand ? (
              <button
                type="button"
                className={styles.appShelfHeading}
                aria-expanded={expanded}
                onClick={toggleExpanded}
              >
                {heading}
              </button>
            ) : (
              heading
            )}
          </Title>
        </Split.Item>
        {hasOverflow && !expanded ? (
          <>
            <Split.Item>
              <IconButton
                variant="outline"
                shape="circular"
                size="small"
                disabled={!overflow.left}
                onClick={() => page('left')}
                aria-label={t('apps_shelf_previous', {name: heading})}
              >
                <UIIcon src={ChevronLeftLine} />
              </IconButton>
            </Split.Item>
            <Split.Item>
              <IconButton
                variant="outline"
                shape="circular"
                size="small"
                disabled={!overflow.right}
                onClick={() => page('right')}
                aria-label={t('apps_shelf_next', {name: heading})}
              >
                <UIIcon src={ChevronRightLine} />
              </IconButton>
            </Split.Item>
          </>
        ) : null}
        {canExpand ? (
          <Split.Item>
            <Button
              variant="transparent"
              size="small"
              aria-expanded={expanded}
              aria-label={t(expanded ? 'apps_shelf_show_less_label' : 'apps_shelf_see_all_label', {
                name: heading,
              })}
              withIconEnd={<UIIcon src={expanded ? ChevronUpLine : ChevronDownLine} />}
              onClick={toggleExpanded}
            >
              {t(expanded ? 'apps_shelf_show_less' : 'apps_shelf_see_all')}
            </Button>
          </Split.Item>
        ) : null}
      </Split>
      <div
        ref={railRef}
        className={
          expanded ? `${styles.appShelfRail} ${styles.appShelfRailExpanded}` : styles.appShelfRail
        }
        onScroll={updateOverflow}
      >
        {apps.map((app, index) => (
          <AppCatalogCard
            key={app.id ?? `${app.slug}-${index}`}
            app={app}
            tileLayout
            withActions={withActions}
          />
        ))}
      </div>
    </section>
  );
});

AppShelf.displayName = 'AppShelf';
